import { checkIsDeliveryRequired, checkPositionsDiscounts, generateInitialPositions, getDeliveryData } from '../utils/utils'
import { DataLayerEvent, Ecommerce, EcommerceItem } from 'types/global'
import useStorage from 'hooks/useStorage'
import { useRef } from 'react'

export const CART_STORAGE_KEY = 'cart'
export const SESSION = true

const useCart = (
  checkPrice?: order.CheckPrice,
  lastOrder?: order.Cart
) => {
  const previousSendedCart = useRef<Ecommerce | null>(null)
  const [_cart, setCart] = useStorage<order.Cart>(
    (() => {
      const positions = generateInitialPositions(checkPrice?.products)
      const isDeliveryRequired = checkIsDeliveryRequired(positions, checkPrice?.products)
      const lastDeliveryOption = lastOrder?.selected_delivery

      const cart: order.Cart = {
        name: lastOrder?.name || '',
        email: lastOrder?.email || '',
        phone: lastOrder?.phone || '',
        payment_method: lastOrder?.payment_method || undefined,
        privacy: false,
        positions
      }

      if (isDeliveryRequired && lastDeliveryOption) {
        cart.selected_delivery = lastDeliveryOption
        if (lastDeliveryOption === 'delivery_point') {
          cart.delivery_point = lastOrder?.delivery_point
        } else if (lastDeliveryOption === 'courier' || lastDeliveryOption === 'cash_on_delivery_courier') {
          cart.courier = lastOrder?.courier
        }
      }

      if (positions.length > 0) {
        sentCartToGtm('add_to_cart', cart)
      }

      return cart
    })(),
    CART_STORAGE_KEY,
    SESSION
  )

  function generateEcommerceItems(cart: order.Cart) {
    let finalPrice = 0
    const discounts = checkPositionsDiscounts(cart.positions, checkPrice?.products)
    const items = cart.positions.reduce((r, { id, amount, addons, params }) => {
      const cost = Number(checkPrice?.products?.[id].order_params.cost || 0)
      const price = cost - (((discounts?.[id]?.current || 0) / 100) * cost)
      const addonsList = Object.entries(addons)
      const element: EcommerceItem = {
        item_id: id,
        quantity: amount,
        price
      }
      if (params) {
        element.relation_id = params.related.id
      }
      if (addonsList.length > 0) {
        r.push(...addonsList.reduce((r, [item_id, quantity]) => {
          const price = Number(checkPrice?.products?.[item_id].order_params.cost || 0)
          if (quantity > 0) {
            r.push({
              item_id,
              quantity,
              price
            })
          }
          finalPrice += (quantity * price)
          return r
        }, [] as EcommerceItem[]))
      }
      finalPrice += (amount * price)
      r.push(element)
      return r
    }, [] as EcommerceItem[])

    return {
      items,
      finalPrice
    }
  }

  function sentCartToGtm(potentialEvent: DataLayerEvent = 'add_to_cart', cart = _cart, transaction_id?: string) {
    const delivery = getDeliveryData(cart.selected_delivery, checkPrice?.products)
    const deliveryCost = delivery?.cost ? Number(delivery?.cost) : 0
    let { finalPrice, items } = generateEcommerceItems(cart)
    let event = potentialEvent

    const ecommerce: Ecommerce = {
      currency: 'PLN',
      items,
      value: 0
    }

    if (transaction_id) {
      ecommerce.transaction_id = transaction_id
    }

    if (cart.selected_delivery) {
      ecommerce.shipping_tier = cart.selected_delivery
    }

    if (cart.payment_method) {
      ecommerce.payment_type = cart.payment_method
    }

    if (cart.discount_code && checkPrice?.discount_code) {
      const finalPriceBeforeDiscount = checkPrice.discount_code.affects_delivery && deliveryCost ? finalPrice + deliveryCost : finalPrice
      const finalPriceWithDiscount = finalPriceBeforeDiscount - (finalPriceBeforeDiscount * ((100 - checkPrice.discount_code.percentage) / 100))
      finalPrice = finalPriceWithDiscount
      if (!checkPrice.discount_code.affects_delivery && deliveryCost) {
        finalPrice += deliveryCost
      }
      ecommerce.coupon = cart.discount_code
    } else {
      finalPrice += deliveryCost
    }


    ecommerce.value = finalPrice
    if (event === 'add_to_cart' &&
      previousSendedCart.current &&
      calculateAmountOfElementsInTheEcommerceCart(previousSendedCart.current.items) > calculateAmountOfElementsInTheEcommerceCart(items)) {
      event = 'remove_from_cart'
    }

    window.dataLayer.push({ ecommerce: null }) // Clear the previous ecommerce object.
    window.dataLayer.push({
      event,
      ecommerce
    })

    previousSendedCart.current = ecommerce
  }

  return [
    _cart,
    setCart,
    sentCartToGtm
  ] as const
}

const calculateAmountOfElementsInTheEcommerceCart = (items: EcommerceItem[]) => {
  return items.reduce((r, e) => r += e.quantity, 0)
}

export default useCart
